import React from 'react';
import { 
  X, 
  Clock, 
  FileText, 
  Calendar,
  ChevronLeft,
  RefreshCw,
  Copy,
  CheckCircle
} from 'lucide-react';
import CBADisplay from './CBADisplay';
import RewriteDisplay from './RewriteDisplay';

const PastReportViewer = ({ report, onClose }) => {
  const [copiedToClipboard, setCopiedToClipboard] = React.useState(false);
  
  if (!report) return null;

  // Format the timestamp
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Copy report to clipboard
  const copyToClipboard = async () => {
    try {
      let textToCopy = '';
      
      // Format the report content based on type
      switch (report.type) {
        case 'cba':
          textToCopy = `Cost-Benefit Analysis\n${report.regulation}\n${formatDate(report.timestamp)}\n\n`;
          if (report.data) {
            textToCopy += `Costs:\n${report.data.costs || 'N/A'}\n\n`;
            textToCopy += `Benefits:\n${report.data.benefits || 'N/A'}\n\n`;
            textToCopy += `Net Impact:\n${report.data.netImpact || 'N/A'}\n\n`;
            textToCopy += `Recommendation:\n${report.data.recommendation || 'N/A'}`;
          }
          break;
        case 'rewrite':
          textToCopy = `Plain Language Rewrite\n${report.regulation}\n${formatDate(report.timestamp)}\n\n`;
          if (report.data && report.data.rewrittenText) {
            textToCopy += report.data.rewrittenText;
          }
          break;
        case 'consolidation':
          textToCopy = `Consolidated Regulation\n${formatDate(report.timestamp)}\n\n`;
          if (report.data && report.data.consolidatedText) {
            // Strip HTML tags for plain text copy
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = report.data.consolidatedText;
            textToCopy += tempDiv.textContent || tempDiv.innerText || '';
          }
          break;
        default:
          textToCopy = JSON.stringify(report, null, 2);
      }
      
      if (report.context) {
        textToCopy += `\n\nContext: ${report.context}`;
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Render the appropriate content based on report type
  const renderReportContent = () => {
    switch (report.type) {
      case 'cba':
        return (
          <div style={{ padding: '20px' }}>
            <CBADisplay 
              cbaData={report.data}
              isLoading={false}
            />
          </div>
        );
      
      case 'rewrite':
        return (
          <div style={{ padding: '20px' }}>
            <RewriteDisplay 
              rewriteData={report.data}
              isLoading={false}
              error={null}
            />
          </div>
        );
      
      case 'bulk-cba':
        return (
          <div style={{ padding: '20px' }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>
                Bulk Cost-Benefit Analysis
              </h3>
              <div style={{
                background: '#f9fafb',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  Analysis of {report.regulationCount || 'multiple'} regulations
                </p>
              </div>
              {report.data && (
                <pre style={{
                  background: '#f3f4f6',
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  overflow: 'auto',
                  maxHeight: '400px'
                }}>
                  {JSON.stringify(report.data, null, 2)}
                </pre>
              )}
            </div>
          </div>
        );
      
      case 'consolidation':
        return (
          <div style={{ padding: '20px' }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>
                Consolidated Regulation
              </h3>
              {report.data && report.data.consolidatedText && (
                <div style={{
                  background: '#f9fafb',
                  padding: '20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.8',
                  color: '#374151'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: report.data.consolidatedText }} />
                </div>
              )}
              {report.data && report.data.summary && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    Summary
                  </h4>
                  <p style={{ fontSize: '13px', color: '#1e40af' }}>
                    Word count reduced by {report.data.summary.wordReduction || 0}%
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            <p>Report type not recognized</p>
          </div>
        );
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          background: 'linear-gradient(to bottom, #ffffff, #f9fafb)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              <ChevronLeft style={{ width: '20px', height: '20px' }} />
            </button>
            
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '4px'
              }}>
                {report.title}
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '13px',
                color: '#6b7280'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FileText style={{ width: '14px', height: '14px' }} />
                  {report.regulation}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Calendar style={{ width: '14px', height: '14px' }} />
                  {formatDate(report.timestamp)}
                </span>
                <span style={{
                  background: report.color + '20',
                  color: report.color,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>
                  {report.category}
                </span>
                {report.context && (
                  <span style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500'
                  }}>
                    With Context
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={copyToClipboard}
              style={{
                padding: '8px 12px',
                background: copiedToClipboard ? '#10b981' : 'white',
                border: '1px solid ' + (copiedToClipboard ? '#10b981' : '#e5e7eb'),
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: copiedToClipboard ? 'white' : '#6b7280',
                transition: 'all 0.2s'
              }}
              title="Copy to clipboard"
            >
              {copiedToClipboard ? (
                <>
                  <CheckCircle style={{ width: '16px', height: '16px' }} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy style={{ width: '16px', height: '16px' }} />
                  Copy to Clipboard
                </>
              )}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                background: '#ef4444',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                transition: 'all 0.2s'
              }}
              title="Close viewer"
            >
              <X style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          background: '#f9fafb'
        }}>
          {renderReportContent()}
        </div>

        {/* Footer with context if available */}
        {report.context && (
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            background: 'linear-gradient(to top, #ffffff, #f9fafb)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <RefreshCw style={{ width: '14px', height: '14px', color: '#8b5cf6' }} />
              <span style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#8b5cf6'
              }}>
                Generated with context
              </span>
            </div>
            <p style={{
              fontSize: '13px',
              color: '#6b7280',
              background: '#f3f4f6',
              padding: '8px 12px',
              borderRadius: '6px',
              margin: 0
            }}>
              {report.context}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastReportViewer;